import { createSelector } from "@reduxjs/toolkit";
import { MenuLookups } from "../../../utils/cart/menuLookup";
import { priceSubItemForFranchise } from "../../../utils/help/pricing/franchisePricing";
import type { RootState } from "../app/store";
import { SubItemType } from "../../../utils/types";

const EMPTY_SUB_ITEM_LOOKUP: Record<string, SubItemType> = {};

const selectStoredSubItemLookup = (state: RootState) =>
  state.config.subItemLookupByUuid ?? EMPTY_SUB_ITEM_LOOKUP;

/** The branch /home and the item page price the menu for. */
const selectMenuFranchiseId = (state: RootState) =>
  state.cartAndLoyaltyReducer?.cart?.franchiseId ?? -1;

/**
 * Sub-items priced for the branch the guest is ordering from.
 *
 * The stored price cannot be trusted. A merge keeps the entry already held (so
 * a hydrated addon tree is not swapped for a menu header), and entries arrive
 * unpriced: /home stores the raw SSG menu before its branch-priced copy, and the
 * offer page stores the raw item it fetches for addons. The base price sticks,
 * persisted across reloads. Offer lines send this price as `unitPrice`, which
 * order/calculate prices with, so the cart showed the base price while
 * create-receipt charged the branch's.
 *
 * Pricing on read from `basePrice` and `customFranchiseSubItemPrices` makes the
 * stored price irrelevant, whatever order the writes landed in.
 */
export const selectSubItemLookup = createSelector(
  [selectStoredSubItemLookup, selectMenuFranchiseId],
  (lookup, franchiseId) => {
    const priced: Record<string, SubItemType> = {};

    for (const [key, subItem] of Object.entries(lookup)) {
      priced[key] = priceSubItemForFranchise(subItem, franchiseId);
    }

    return priced;
  }
);

export const selectAddonSubItemLookup = (state: RootState) =>
  state.config.addonSubItemLookupByUuid ?? {};

export const selectAddonGroupLookup = (state: RootState) =>
  state.config.addonGroupLookupByUuid ?? {};

export const selectMenuLookups: (state: RootState) => MenuLookups =
  createSelector(
    [selectSubItemLookup, selectAddonSubItemLookup, selectAddonGroupLookup],
    (subItemsByUuid, addonSubItemsByUuid, addonGroupsByUuid) => ({
      subItemsByUuid,
      addonSubItemsByUuid,
      addonGroupsByUuid,
    })
  );

export const selectResolveMenuSubItem = createSelector(
  [selectMenuLookups],
  (menuLookups) =>
    (entityId: string): SubItemType | undefined =>
      menuLookups.subItemsByUuid[entityId] ??
      menuLookups.addonSubItemsByUuid[entityId]
);
